/**
 * Where the sun was when a clip was shot — so "find my sunset flights" is a real query.
 *
 * A fixed clock bucket cannot answer it: in Turkey sunset moves from ~16:40 in December to ~20:45 in
 * June, so "18:00 = sunset" is wrong for most of the year. The honest definition is the sun's
 * elevation above the horizon at the takeoff point, which needs the instant, the latitude and the
 * longitude — all of which a flight already carries.
 *
 * Implements NOAA's low-precision solar-position algorithm (the one behind the NOAA Solar Calculator,
 * itself following Meeus, *Astronomical Algorithms*, ch. 25 & 28). Accurate to ~0.01° in declination
 * and well under a minute on sunrise/sunset for any year and latitude this app sees — far more than
 * "was this golden hour?" needs, and with no dependency.
 *
 * Reference: https://gml.noaa.gov/grad/solcalc/calcdetails.html
 * Verified against Open-Meteo's published sunrise/sunset — see test/solar.test.mjs.
 */

const RAD = Math.PI / 180;
const sin = (deg: number): number => Math.sin(deg * RAD);
const cos = (deg: number): number => Math.cos(deg * RAD);
const mod360 = (deg: number): number => ((deg % 360) + 360) % 360;

/**
 * Sun's geometric position as seen from `lat`/`lon` at `atMs` (epoch ms).
 *
 * `elevDeg` is the GEOMETRIC elevation of the sun's centre — no refraction, no semi-diameter. That is
 * deliberate: the twilight and golden-hour thresholds below are all defined against geometric
 * elevation, so correcting here would silently shift every boundary by ~0.5°.
 *
 * `hourAngleDeg` is 0 at solar noon, negative while the sun is still climbing (morning) and positive
 * once it is descending (afternoon) — the cheapest reliable way to tell dawn from dusk, since the two
 * are indistinguishable by elevation alone.
 */
export function solarPosition(atMs: number, lat: number, lon: number): { elevDeg: number; hourAngleDeg: number; declDeg: number } {
  const jd = atMs / 86400000 + 2440587.5; // Unix epoch = JD 2440587.5
  const T = (jd - 2451545) / 36525; // Julian centuries since J2000.0
  const L0 = mod360(280.46646 + T * (36000.76983 + T * 0.0003032)); // geometric mean longitude
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T); // geometric mean anomaly
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T); // orbital eccentricity
  const C = sin(M) * (1.914602 - T * (0.004817 + 0.000014 * T)) + sin(2 * M) * (0.019993 - 0.000101 * T) + sin(3 * M) * 0.000289; // equation of the centre
  const omega = 125.04 - 1934.136 * T; // longitude of the Moon's ascending node (drives the nutation terms)
  const appLon = L0 + C - 0.00569 - 0.00478 * sin(omega); // apparent longitude (aberration + nutation)
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60; // mean obliquity
  const eps = eps0 + 0.00256 * cos(omega); // obliquity, nutation-corrected
  const declDeg = Math.asin(sin(eps) * sin(appLon)) / RAD;

  // Equation of time (minutes): apparent solar time minus mean solar time.
  const y = Math.tan((eps * RAD) / 2) ** 2;
  const eqTime = (4 * (y * sin(2 * L0) - 2 * e * sin(M) + 4 * e * y * sin(M) * cos(2 * L0) - 0.5 * y * y * sin(4 * L0) - 1.25 * e * e * sin(2 * M))) / RAD;

  // True solar time at the observer's longitude, in minutes past solar midnight: UTC minutes of day,
  // shifted by the equation of time and by 4 minutes per degree of longitude east.
  const utcMin = ((((atMs % 86400000) + 86400000) % 86400000) / 60000);
  let hourAngleDeg = (utcMin + eqTime + 4 * lon) / 4 - 180;
  while (hourAngleDeg < -180) hourAngleDeg += 360;
  while (hourAngleDeg > 180) hourAngleDeg -= 360;

  const cosZen = sin(lat) * sin(declDeg) + cos(lat) * cos(declDeg) * cos(hourAngleDeg);
  const zenDeg = Math.acos(Math.min(1, Math.max(-1, cosZen))) / RAD;
  return { elevDeg: 90 - zenDeg, hourAngleDeg, declDeg };
}

/** Convenience wrapper — geometric elevation of the sun's centre, in degrees. */
export const solarElevationDeg = (atMs: number, lat: number, lon: number): number => solarPosition(atMs, lat, lon).elevDeg;

/**
 * The eight phases a flight can fall into, in the order the sun walks through them. Split by elevation
 * and by whether the sun is rising or setting, so dawn/dusk and morning/afternoon stay distinct.
 */
export type SunPhase = 'night' | 'dawn' | 'sunrise' | 'morning' | 'noon' | 'afternoon' | 'sunset' | 'dusk';

/** Hour angle within ±1 h of solar noon counts as midday (the harsh-light window). */
const NOON_HOUR_ANGLE = 15;

/**
 * Photographic phase boundaries, in geometric elevation of the sun's centre:
 *
 *   below −6°   night          (civil twilight has ended — the sky is dark)
 *   −6° … −4°   blue hour      (dawn while rising, dusk while setting)
 *   −4° … +6°   golden hour    (sunrise while rising, sunset while setting)
 *   above +6°   full daylight  (morning / midday / afternoon)
 *
 * −6° is the standard civil-twilight limit; −4°/+6° are the conventional blue- and golden-hour
 * bounds. All three are defined against geometric elevation, which is what `solarPosition` returns.
 */
export function sunPhase(atMs: number, lat: number, lon: number): SunPhase {
  const { elevDeg, hourAngleDeg } = solarPosition(atMs, lat, lon);
  const rising = hourAngleDeg < 0;
  if (elevDeg < -6) return 'night';
  if (elevDeg < -4) return rising ? 'dawn' : 'dusk';
  if (elevDeg < 6) return rising ? 'sunrise' : 'sunset';
  if (Math.abs(hourAngleDeg) <= NOON_HOUR_ANGLE) return 'noon';
  return rising ? 'morning' : 'afternoon';
}

/**
 * Display metadata per phase, in sun order (night → dawn → … → dusk). `label` is the English i18n key
 * (the component wraps it in `t()`); `hint` explains the boundary in the filter popup; `words` are
 * extra search synonyms so typing "golden hour" or "twilight" finds the right flights.
 */
export const SUN_PHASES: { key: SunPhase; icon: string; label: string; hint: string; words: string }[] = [
  { key: 'night', icon: '🌑', label: 'Night', hint: 'sun below −6°', words: 'night dark gece karanlık' },
  { key: 'dawn', icon: '🌌', label: 'Dawn (blue hour)', hint: 'rising, −6° … −4°', words: 'dawn blue hour twilight şafak mavi saat alacakaranlık tan' },
  { key: 'sunrise', icon: '🌅', label: 'Sunrise (golden hour)', hint: 'rising, −4° … +6°', words: 'sunrise golden hour gün doğumu altın saat şafak' },
  { key: 'morning', icon: '🌤', label: 'Morning', hint: 'rising, above +6°', words: 'morning sabah kuşluk' },
  { key: 'noon', icon: '☀️', label: 'Midday', hint: 'within 1 h of solar noon', words: 'midday noon öğle tepe güneş' },
  { key: 'afternoon', icon: '⛅', label: 'Afternoon', hint: 'setting, above +6°', words: 'afternoon ikindi öğleden sonra' },
  { key: 'sunset', icon: '🌇', label: 'Sunset (golden hour)', hint: 'setting, +6° … −4°', words: 'sunset golden hour gün batımı altın saat akşam' },
  { key: 'dusk', icon: '🌃', label: 'Dusk (blue hour)', hint: 'setting, −4° … −6°', words: 'dusk blue hour twilight alacakaranlık mavi saat akşam karanlığı' },
];

const PHASE_META = new Map(SUN_PHASES.map((p) => [p.key, p]));
/** Icon for a phase — '' when the phase is unknown (no coordinates or no timestamp). */
export const sunPhaseIcon = (p: SunPhase | null): string => (p ? (PHASE_META.get(p)?.icon ?? '') : '');
/** English i18n key for a phase — '' when unknown. */
export const sunPhaseLabel = (p: SunPhase | null): string => (p ? (PHASE_META.get(p)?.label ?? '') : '');
